import { useCallback, useState } from "react";

import { FloatingButtons } from "../../../../components/common/FloatingButtons";
import type { IRAOnboardingProps } from "..";
import { Affiliations } from "./Affiliations";
import { BasicInformation } from "./BasicInformation";
import { FinancialProfile } from "./FinancialProfile";
import { PersonalProfile } from "./PersonalProfile";
import { ResidencyVisaStatus } from "./ResidencyVisaStatus";

const isPersonalProfileComplete = (
  details: IRAOnboardingProps["tradeAccountDetails"],
) => !!details.marital_status && details.number_of_dependents != null;

const isFinancialProfileComplete = (
  details: IRAOnboardingProps["tradeAccountDetails"],
  account: IRAOnboardingProps["tradeAccount"],
) =>
  !!details.employment_status &&
  !!details.funding_source?.length &&
  !!details.investment_experience_with_stocks &&
  !!details.investment_experience_with_options &&
  !!account.investment_objective;

type EditableSection = "affiliations" | "financialProfile" | "personalProfile";

export const ConfirmInformation = ({
  tradeAccount,
  tradeAccountDetails,
  refetchTradeAccountDetails,
  refetchTradeAccount,
  onSaveSessionState,
}: IRAOnboardingProps) => {
  const [editingSections, setEditingSections] = useState<Set<EditableSection>>(
    () => {
      const initial = new Set<EditableSection>();
      // default to editing mode when lacking data
      if (!isFinancialProfileComplete(tradeAccountDetails, tradeAccount)) {
        initial.add("financialProfile");
      }
      if (!isPersonalProfileComplete(tradeAccountDetails)) {
        initial.add("personalProfile");
      }
      return initial;
    },
  );

  const [openSections, setOpenSections] = useState<Set<EditableSection>>(
    // Open by default
    new Set<EditableSection>([
      "affiliations",
      "financialProfile",
      "personalProfile",
    ]),
  );

  const toggleSection = useCallback(
    (section: EditableSection, value: boolean) => {
      setOpenSections((prev) => {
        const next = new Set(prev);
        if (value) {
          next.add(section);
        } else {
          next.delete(section);
        }
        return next;
      });
    },
    [],
  );

  const setIsEditing = useCallback(
    (section: EditableSection, editing: boolean) => {
      setEditingSections((prev) => {
        const next = new Set(prev);
        if (editing) {
          next.add(section);
        } else {
          next.delete(section);
        }
        return next;
      });
    },
    [],
  );

  const canContinue =
    editingSections.size === 0 &&
    isPersonalProfileComplete(tradeAccountDetails) &&
    isFinancialProfileComplete(tradeAccountDetails, tradeAccount);

  return (
    <div className="flex h-full w-full flex-col justify-start gap-8">
      <BasicInformation tradeAccountDetails={tradeAccountDetails} />
      <ResidencyVisaStatus tradeAccountDetails={tradeAccountDetails} />
      <Affiliations
        tradeAccount={tradeAccount}
        tradeAccountDetails={tradeAccountDetails}
        refetchTradeAccountDetails={refetchTradeAccountDetails}
        isEditing={editingSections.has("affiliations")}
        setIsEditing={(editing) => setIsEditing("affiliations", editing)}
        isOpen={openSections.has("affiliations")}
        setIsOpen={(open) => toggleSection("affiliations", open)}
      />
      <FinancialProfile
        tradeAccount={tradeAccount}
        tradeAccountDetails={tradeAccountDetails}
        refetchTradeAccountDetails={refetchTradeAccountDetails}
        refetchTradeAccount={refetchTradeAccount}
        isEditing={editingSections.has("financialProfile")}
        setIsEditing={(editing) => setIsEditing("financialProfile", editing)}
        isOpen={openSections.has("financialProfile")}
        setIsOpen={(open) => toggleSection("financialProfile", open)}
      />
      <PersonalProfile
        tradeAccount={tradeAccount}
        tradeAccountDetails={tradeAccountDetails}
        refetchTradeAccountDetails={refetchTradeAccountDetails}
        isEditing={editingSections.has("personalProfile")}
        setIsEditing={(editing) => setIsEditing("personalProfile", editing)}
        isOpen={openSections.has("personalProfile")}
        setIsOpen={(open) => toggleSection("personalProfile", open)}
      />
      <div>
        <FloatingButtons
          fixedOnBottom
          className="bg-white"
          next={{
            onClick: () => {
              onSaveSessionState({ step: "beneficiaries" });
            },
            disabled: !canContinue,
          }}
          back={{
            onClick: () => {
              onSaveSessionState({ step: "type" });
            },
          }}
        />
      </div>
    </div>
  );
};
